import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, Layers, Cpu, TrendingUp, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useAppStore } from '../store';
import { MACHINES } from '../engineeringRules';

export default function Dashboard() {
  const jobs = useAppStore(s => s.jobs);

  const stats = {
    total: jobs.length,
    draft: jobs.filter(j => j.status === 'DRAFT').length,
    inProgress: jobs.filter(j => ['PARSING','REVIEW','PLANNING'].includes(j.status)).length,
    complete: jobs.filter(j => ['APPROVED','COMPLETE'].includes(j.status)).length,
  };

  const recentJobs = jobs.slice(0, 5);
  const machines = Object.values(MACHINES);
  const nutFormers = machines.filter(m => m.type === 'NUT');
  const boltFormers = machines.filter(m => m.type === 'BOLT');

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1>Dashboard</h1>
            <p style={{ color: 'var(--forge-text2)', fontSize: 12, marginTop: 4 }}>
              Cold forging process planning — Sturdfix / Singhania International
            </p>
          </div>
          <Link to="/new-job" className="btn btn-primary btn-lg">
            <Zap size={14} /> New Job
          </Link>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        {[
          { label: 'Total Jobs', value: stats.total, icon: Layers, color: 'var(--forge-blue)' },
          { label: 'In Progress', value: stats.inProgress, icon: Clock, color: 'var(--forge-yellow)' },
          { label: 'Completed', value: stats.complete, icon: CheckCircle, color: 'var(--forge-green)' },
          { label: 'Drafts', value: stats.draft, icon: AlertCircle, color: 'var(--forge-text3)' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="card">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 28, fontWeight: 800, fontFamily: 'var(--font-display)', color, letterSpacing: '-0.04em' }}>{value}</div>
                <div style={{ fontSize: 11, color: 'var(--forge-text2)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
              </div>
              <Icon size={16} color={color} />
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20 }}>
        {/* Recent Jobs */}
        <div className="card">
          <div className="card-header">
            <h3>Recent Jobs</h3>
            <Link to="/jobs" className="btn btn-ghost btn-sm">View all</Link>
          </div>
          {recentJobs.length === 0 ? (
            <div className="empty-state">
              <Layers size={32} />
              <p style={{ marginTop: 12 }}>No jobs yet</p>
              <Link to="/new-job" className="btn btn-primary btn-sm" style={{ marginTop: 12 }}>
                <Zap size={12} /> Create first job
              </Link>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Part</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {recentJobs.map(job => (
                  <tr key={job.id} style={{ cursor: 'pointer' }}>
                    <td><Link to={`/jobs/${job.id}`} style={{ color: 'var(--forge-accent)', textDecoration: 'none', fontFamily: 'var(--font-mono)' }}>{job.id}</Link></td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{job.part_no || '—'}</td>
                    <td style={{ color: 'var(--forge-text2)' }}>{job.customer}</td>
                    <td><StatusBadge status={job.status} /></td>
                    <td style={{ color: 'var(--forge-text3)', fontSize: 11 }}>{new Date(job.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Machine Fleet */}
        <div className="card">
          <div className="card-header"><h3>Machine Fleet</h3></div>
          <h4 style={{ marginBottom: 10 }}>Bolt Formers</h4>
          {boltFormers.map(m => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--forge-border)' }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--forge-text)' }}>{m.name}</div>
                <div style={{ fontSize: 10, color: 'var(--forge-text3)' }}>Ø{m.wire_min}–{m.wire_max}mm · {m.stations}stn</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: 'var(--forge-text3)' }}>{(m.force_kg/1000).toFixed(0)}t</div>
                <span className="badge badge-green" style={{ marginTop: 2 }}>×{m.qty}</span>
              </div>
            </div>
          ))}
          <h4 style={{ marginTop: 16, marginBottom: 10 }}>Nut Formers</h4>
          {nutFormers.map(m => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--forge-border)' }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--forge-text)' }}>{m.name}</div>
                <div style={{ fontSize: 10, color: 'var(--forge-text3)' }}>{(m as any).nut_size} · {m.stations}stn</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: 'var(--forge-text3)' }}>{(m.force_kg/1000).toFixed(0)}t</div>
                <span className="badge badge-blue" style={{ marginTop: 2 }}>×{m.qty}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { cls: string; label: string }> = {
    DRAFT: { cls: 'badge-gray', label: 'Draft' },
    PARSING: { cls: 'badge-yellow', label: 'Parsing' },
    REVIEW: { cls: 'badge-blue', label: 'Review' },
    PLANNING: { cls: 'badge-orange', label: 'Planning' },
    APPROVED: { cls: 'badge-green', label: 'Approved' },
    COMPLETE: { cls: 'badge-green', label: 'Complete' },
  };
  const { cls, label } = map[status] ?? { cls: 'badge-gray', label: status };
  return <span className={`badge ${cls}`}>{label}</span>;
}
